import { VenueReservation } from "@/graphql/operations";
import useDevice from "@/hooks/useDevice";
import useLocalization from "@/hooks/useLocalization";
import icons from "@/tools/icons";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import ReservationRow from "./ReservationRow/ReservationRow";

type PendingReservationListProps = {
  reservations: VenueReservation[];
  pendingCount?: number;
  selectedReservation: VenueReservation | null;
  selectReservation: (reservationId: string) => void;
};

export function PendingReservationList({
  reservations,
  pendingCount,
  selectReservation,
  selectedReservation,
}: PendingReservationListProps) {
  const { isMobile } = useDevice();
  const [expanded, setExpanded] = useState(true);
  const { t } = useLocalization();
  const displayCount = pendingCount ?? reservations.length;

  return (
    <Accordion
      data-testid="pending-reservations-list"
      disableGutters
      expanded={expanded}
      onChange={(_, nextExpanded) => setExpanded(nextExpanded)}
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        boxShadow: "none",
        overflow: isMobile ? "visible" : "hidden",
        "& .MuiAccordionSummary-content": {
          marginTop: "4px",
          marginBottom: "4px",
        },
      }}
    >
      <AccordionSummary
        sx={{ backgroundColor: "background.default" }}
        expandIcon={<icons.ExpandMore color="inherit" />}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Typography variant="h6">
            <Avatar
              sx={{
                display: "inline-flex",
                bgcolor: "secondary.main",
                height: 24,
                width: 24,
              }}
            >
              {displayCount}
            </Avatar>{" "}
            {t("reservation:pendingReservations")}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: 0,
          overflowY: isMobile ? "visible" : "auto",
          maxHeight: isMobile ? "none" : "40vh",
        }}
      >
        {reservations.map((reservation) => (
          <Box key={reservation.id} data-testid="pending-reservation-row">
            <ReservationRow
              reservation={reservation}
              isSelected={selectedReservation?.id === reservation.id}
              selectReservation={() => selectReservation(reservation.id)}
            />
            <Divider />
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  );
}
